import Box from '@mui/material/Box';
import { useMemo } from 'react';

import {
	getCollectionEntries,
	toItemFieldId
} from '@/core/dcr/fields/registry';
import type { CollectionId, ItemFieldDef } from '@/core/dcr/fields/types';

import { useDcrFormContext } from '../_context/DcrFormContext';
import { PicklistInput } from '../DcrField/renderers/PicklistInput';
import { TextInput } from '../DcrField/renderers/TextInput';

import { collectionCardStyles as sx } from './Collections.styles';
import { HcoSearchItemInput } from './HcoSearchItemInput';
import { PhoneItemInput } from './PhoneItemInput';

const joinPath = (path: string, key: string) => (path ? `${path}.${key}` : key);

const ItemField = ({
	path,
	itemKey,
	def
}: {
	path: string;
	itemKey: string;
	def: ItemFieldDef;
}) => {
	const name = joinPath(path, itemKey);

	switch (def.kind) {
		case 'phone':
			return def.isoKey ? (
				<PhoneItemInput
					name={name}
					isoName={joinPath(path, def.isoKey)}
					def={def}
				/>
			) : (
				<TextInput name={name} def={def} />
			);
		case 'picklist':
			return <PicklistInput name={name} def={def} />;
		case 'hcoSearch':
			return (
				<HcoSearchItemInput
					name={name}
					displayName={joinPath(path, 'parentHco')}
					def={def}
				/>
			);
		case 'text':
		case 'date':
			return <TextInput name={name} def={def} />;
		default:
			return null;
	}
};

type Props = {
	collectionKey: string;
	collectionId: CollectionId;
	path: string;
	keys?: readonly string[];
};

export const CollectionItemFields = ({
	collectionKey,
	collectionId,
	path,
	keys
}: Props) => {
	const { isAvailable, isAllowed } = useDcrFormContext();

	const entries = useMemo(
		() =>
			getCollectionEntries(collectionId).filter(({ key, def }) => {
				const id = toItemFieldId(collectionKey, key);
				return keys
					? keys.includes(key) && isAllowed(id, def)
					: def.visibility === 'always' && isAvailable(id, def);
			}),
		[collectionId, collectionKey, isAllowed, isAvailable, keys]
	);

	return (
		<Box sx={sx.grid}>
			{entries.map(({ key, def }) => (
				<ItemField key={key} path={path} itemKey={key} def={def} />
			))}
		</Box>
	);
};
